import { useEffect, useState } from 'react';

import type { Fork } from '../lib/protocol';

/** Объект `pp` вилки forq-predict (backend/forq/predict/wire.py). */
interface Facts {
  kind?: 'pair' | 'set';
  counted?: boolean;
  short?: string | null;
  days?: string | null;
  apy?: string | null;
  vol?: string;
  vol2?: string;
  ages?: (number | null)[];
  hot?: boolean[];
  why?: string;
  oracle?: Record<string, string>;
  currency?: string;
  links?: Record<string, string>;
  also?: { name: string; url: string }[];
}

const VENUE: Record<string, string> = {
  polymarket: 'Polymarket', predictfun: 'Predict.fun', jetton: 'Jetton', kalshi: 'Kalshi',
};

export function factsOf(fork: Fork): Facts | null {
  const raw = fork.pp;
  return raw !== null && typeof raw === 'object' ? (raw as Facts) : null;
}

function ageText(age: number | null | undefined, hot: boolean | undefined, since: number): string {
  if (age === null || age === undefined) return 'нет книги';
  const now = age + since;
  const shown = now < 10 ? now.toFixed(1) : String(Math.round(now));
  return `${shown} с${hot ? ', живая' : ', опрос'}`;
}

/** Строка предикт-вилки: срок, годовая, объём, возраст каждой ноги, почему пара склеена и как считают исход. */
export function PredictFacts({ fork }: { fork: Fork }): React.ReactElement | null {
  const facts = factsOf(fork);
  const [since, setSince] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => setSince((Date.now() - started) / 1000), 1000);
    return () => clearInterval(timer);
  }, [fork]);
  if (facts === null) return null;
  const legs = fork.legs ?? [];
  const oracle = Object.entries(facts.oracle ?? {});
  const also = facts.also ?? [];
  return (
    <dl className="nx-det__pp">
      {facts.days != null && (
        <>
          <dt>до расчёта</dt>
          <dd>
            {facts.days} дн.{facts.apy != null ? ` · годовых ${facts.apy} %` : ''}
          </dd>
        </>
      )}
      <dt>объём</dt>
      <dd>
        ${facts.vol ?? '—'} от 1 % · ${facts.vol2 ?? '—'} от 2 %
        {facts.counted ? ' · в зачёте' : facts.short ? ` · не в зачёте: ${facts.short}` : ''}
      </dd>
      <dt>цены</dt>
      <dd>
        {legs.map((leg, i) => (
          <span key={`${leg.s}-${i}`}>
            {i > 0 ? ' · ' : ''}
            {VENUE[leg.s] ?? leg.s}: {ageText(facts.ages?.[i], facts.hot?.[i], since)}
          </span>
        ))}
      </dd>
      {facts.why && (
        <>
          <dt>склейка</dt>
          <dd>{facts.why}</dd>
        </>
      )}
      {oracle.length > 0 && (
        <>
          <dt>расчёт</dt>
          <dd>
            {oracle.map(([venue, how]) => `${venue} — ${how}`).join(' · ')}
            {facts.currency ? ` · ${facts.currency}` : ''}
          </dd>
        </>
      )}
      {also.length > 0 && (
        <>
          <dt>то же в</dt>
          <dd>
            {also.map((one, i) => (
              <span key={one.name}>
                {i > 0 ? ', ' : ''}
                <a href={one.url} target="_blank" rel="noreferrer">{one.name}</a>
              </span>
            ))}{' '}
            — общий фид Betby, кэфы те же
          </dd>
        </>
      )}
    </dl>
  );
}
